import { create } from 'zustand';

const STORAGE_KEY = 'playground_recent_endpoints';
const MAX_ENTRIES = 10;

export interface RecentEntry {
    operationId: string;
    method: string;
    path: string;
    summary: string;
    ts: number;
}

interface State {
    entries: RecentEntry[];
    add: (entry: Omit<RecentEntry, 'ts'> & { ts?: number }) => void;
    clear: () => void;
    loadFromStorage: () => void;
}

function persist(entries: RecentEntry[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        /* private mode / quota — ignore */
    }
}

function readStorage(): RecentEntry[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Validate entries
        return parsed
            .filter((e: unknown): e is RecentEntry => {
                if (!e || typeof e !== 'object') return false;
                const r = e as Record<string, unknown>;
                return (
                    typeof r.operationId === 'string'
                    && typeof r.method === 'string'
                    && typeof r.path === 'string'
                    && typeof r.summary === 'string'
                    && typeof r.ts === 'number'
                );
            })
            .slice(0, MAX_ENTRIES);
    } catch {
        return [];
    }
}

export const useRecentStore = create<State>((set, get) => ({
    entries: [],
    add: (entry) => {
        const ts = entry.ts ?? Date.now();
        const next: RecentEntry = {
            operationId: entry.operationId,
            method: entry.method,
            path: entry.path,
            summary: entry.summary,
            ts,
        };
        const current = get().entries;
        // Dedupe by operationId — remove existing first
        const filtered = current.filter((e) => e.operationId !== next.operationId);
        // Prepend and cap
        const entries = [next, ...filtered].slice(0, MAX_ENTRIES);
        persist(entries);
        set({ entries });
    },
    clear: () => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            /* */
        }
        set({ entries: [] });
    },
    loadFromStorage: () => {
        const entries = readStorage();
        set({ entries });
    },
}));
